import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight, Award, Sparkles } from 'lucide-react';
import type { TutorialType } from './TutorialProvider';
import { cn } from '@/lib/utils';
import logoImage from '@/assets/chopperswap-logo-no-bg.png';

interface TutorialCompleteProps {
  isOpen: boolean;
  tutorialType: TutorialType | null;
  wasSkipped?: boolean;
  onClose: () => void;
}

const completionMessages: Record<TutorialType, { title: string; message: string; nextStep: string; nextRoute?: string }> = {
  home: {
    title: 'Welcome Tour Complete',
    message: 'You now know the basics. Ready to explore the marketplace?',
    nextStep: 'Browse Marketplace',
    nextRoute: '/marketplace',
  },
  marketplace: {
    title: 'Marketplace Mastered',
    message: 'You\'re all set to find your perfect aircraft, engine, or parts.',
    nextStep: 'Start Searching',
    nextRoute: '/marketplace',
  },
  helicopterWizard: {
    title: 'Wizard Tour Complete',
    message: 'You\'re ready to create a professional helicopter listing.',
    nextStep: 'Create Listing',
    nextRoute: '/create-listing?type=sale',
  },
  engineWizard: {
    title: 'Engine Wizard Mastered',
    message: 'Create detailed engine listings with confidence.',
    nextStep: 'List an Engine',
    nextRoute: '/create-listing-engine?type=engine_sell',
  },
  partsWizard: {
    title: 'Parts Wizard Complete',
    message: 'You know how to list parts with all the right details.',
    nextStep: 'List Parts',
    nextRoute: '/create-listing-parts?type=parts_sell_general',
  },
  profile: {
    title: 'Profile Navigation Complete',
    message: 'You can now navigate your account sections like a pro.',
    nextStep: 'Go to Profile',
    nextRoute: '/profile',
  },
  messaging: {
    title: 'Messaging Mastered',
    message: 'Communicate securely with buyers and sellers.',
    nextStep: 'View Messages',
    nextRoute: '/messages',
  },
  verification: {
    title: 'Verification Complete',
    message: 'You know how to earn your verified badge through LinkedIn.',
    nextStep: 'Get Verified',
    nextRoute: '/profile?section=verification',
  },
  savedCompare: {
    title: 'Compare Feature Mastered',
    message: 'You can now save favorites and compare listings side-by-side.',
    nextStep: 'Try Comparing',
    nextRoute: '/marketplace',
  },
  jobWizard: {
    title: 'Job Wizard Mastered',
    message: 'You\'re ready to create professional job listings and profiles.',
    nextStep: 'Create Job Listing',
    nextRoute: '/create-listing-jobs?type=job_post',
  },
  serviceWizard: {
    title: 'Service Wizard Complete',
    message: 'You know how to list aviation services with all the right details.',
    nextStep: 'List a Service',
    nextRoute: '/create-listing-services?type=service_offer',
  },
  privacy: {
    title: 'Privacy Mastered',
    message: 'You now know how to protect your sensitive data and control visibility across all listings.',
    nextStep: 'Review Settings',
    nextRoute: '/profile?section=security',
  },
  alerts: {
    title: 'Alerts Set Up',
    message: 'You\'re ready to create custom alerts and never miss a matching listing.',
    nextStep: 'Create an Alert',
    nextRoute: '/profile?section=alerts',
  },
  listingManagement: {
    title: 'Dashboard Mastered',
    message: 'You can now manage, track, and optimize all your listings from one place.',
    nextStep: 'Go to Dashboard',
    nextRoute: '/profile?section=dashboard',
  },
};

// Confetti particle - primary blue + emerald
const ConfettiParticle: React.FC<{ index: number; delay: number }> = ({ index, delay }) => {
  const angle = (index / 20) * 360;
  const distance = 80 + Math.random() * 60;
  const x = Math.cos((angle * Math.PI) / 180) * distance;
  const y = Math.sin((angle * Math.PI) / 180) * distance;
  const rotation = Math.random() * 360;
  const isAccent = index % 3 !== 0;
  
  return (
    <div
      className="absolute rounded-sm"
      style={{
        left: '50%',
        top: '50%',
        width: isAccent ? '6px' : '4px',
        height: isAccent ? '10px' : '8px',
        backgroundColor: isAccent 
          ? 'hsl(var(--primary))'
          : 'hsl(152, 69%, 45%)', // emerald-500
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        animation: `confettiBurst 0.8s ease-out forwards`,
        animationDelay: `${delay}ms`,
        opacity: 0,
        ['--burst-x' as string]: `${x}px`,
        ['--burst-y' as string]: `${y}px`,
      }}
    />
  );
};

export const TutorialComplete: React.FC<TutorialCompleteProps> = ({
  isOpen,
  tutorialType,
  wasSkipped = false,
  onClose,
}) => {
  const navigate = useNavigate();
  const [showEffects, setShowEffects] = useState(false);
  const info = tutorialType ? completionMessages[tutorialType] : null;

  useEffect(() => {
    if (isOpen && !wasSkipped) {
      const timer = setTimeout(() => setShowEffects(true), 100);
      const cleanup = setTimeout(() => setShowEffects(false), 1500);
      return () => {
        clearTimeout(timer);
        clearTimeout(cleanup);
      };
    } else {
      setShowEffects(false);
    }
  }, [isOpen, wasSkipped]);

  const handleCtaClick = () => {
    onClose();
    if (info?.nextRoute) {
      navigate(info.nextRoute);
    }
  };

  if (!info) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md overflow-hidden border border-primary/30 bg-white dark:bg-slate-900 shadow-xl">
        {/* Confetti effect container — only on completion, not skip */}
        {showEffects && !wasSkipped && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 20 }).map((_, i) => (
              <ConfettiParticle key={`confetti-${i}`} index={i} delay={i * 30} />
            ))}
          </div>
        )}

        <DialogHeader className="text-center relative pt-6">
          {/* Medal with logo design */}
          <div className="mx-auto relative mb-4">
            {/* Outer medal ring - emerald success */}
            <div className={cn(
              "w-28 h-28 rounded-full flex items-center justify-center relative",
              "bg-gradient-to-br from-emerald-500 via-emerald-500/90 to-emerald-600",
              "shadow-lg shadow-emerald-500/40",
              "transition-transform duration-500",
              showEffects && "animate-[medalPop_0.5s_ease-out]"
            )}>
              {/* Inner white circle */}
              <div className="w-20 h-20 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center shadow-inner">
                <img 
                  src={logoImage} 
                  alt="ChopperSwap" 
                  className="w-14 h-14 object-contain dark:brightness-0 dark:invert"
                />
              </div>
              
              {/* Medal ribbon accent */}
              <Award 
                className={cn(
                  "w-8 h-8 text-emerald-500 absolute -bottom-2 -right-2",
                  "drop-shadow-lg",
                  showEffects && "animate-[sparkleIn_0.6s_ease-out_0.2s_both]"
                )}
                fill="currentColor"
              />
              
              {/* Sparkle accent */}
              <Sparkles 
                className={cn(
                  "w-5 h-5 text-primary absolute -top-1 -left-1",
                  "drop-shadow-sm",
                  showEffects && "animate-[sparkleIn_0.6s_ease-out_0.3s_both]"
                )}
              />
            </div>
          </div>
          
          <DialogTitle className="text-2xl font-bold text-primary">
            {info.title}
          </DialogTitle>
          <DialogDescription className="text-base mt-2 text-slate-600 dark:text-slate-300">
            {info.message}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center py-4">
          <div className={cn(
            "flex items-center gap-3 px-5 py-2.5 rounded-full",
            wasSkipped ? "bg-amber-500/10 border border-amber-500/20" : "bg-emerald-500/10 border border-emerald-500/20"
          )}>
            <CheckCircle2 className={cn("w-5 h-5", wasSkipped ? "text-amber-500" : "text-emerald-500")} />
            <span className={cn("text-sm font-semibold", wasSkipped ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400")}>
              {wasSkipped ? 'Tutorial skipped — restart anytime from the help menu' : 'Tutorial completed & saved'}
            </span>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 relative">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Close
          </Button>
          <Button
            onClick={handleCtaClick}
            className={cn(
              "gap-2 font-semibold text-white",
              "bg-primary hover:bg-primary/90",
              "shadow-lg shadow-primary/30",
              "transition-all duration-300"
            )}
          >
            {info.nextStep}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
      
      {/* Clean, professional animations */}
      <style>{`
        @keyframes confettiBurst {
          0% {
            transform: translate(-50%, -50%) scale(0) rotate(0deg);
            opacity: 1;
          }
          60% {
            opacity: 0.9;
          }
          100% {
            transform: translate(calc(-50% + var(--burst-x)), calc(-50% + var(--burst-y))) scale(1) rotate(180deg);
            opacity: 0;
          }
        }
        
        @keyframes medalPop {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes sparkleIn {
          0% { transform: scale(0) rotate(-45deg); opacity: 0; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>
    </Dialog>
  );
};